import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from "@google/generative-ai";

const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY;

if (!geminiApiKey) {
  throw new Error('Variável de ambiente do Gemini não configurada');
}

const genAI = new GoogleGenerativeAI(geminiApiKey);

const PRIMARY_MODEL_NAME = "gemini-2.5-pro-exp-03-25"; // Mantendo o modelo do arquivo original como primário
const FALLBACK_MODEL_NAME = "gemini-2.5-pro-preview-05-06";
const SECOND_FALLBACK_MODEL_NAME = "gemini-2.5-flash-preview-04-17";

// Função auxiliar para gerar conteúdo com um modelo específico
async function generateContentWithModel(
  modelName: string,
  prompt: string,
  imageParts: { inlineData: { data: string; mimeType: string; } }[]
) {
  const model = genAI.getGenerativeModel({
    model: modelName,
    safetySettings: [
      { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
      { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
      { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
      { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
    ],
  });
  const result = await model.generateContent([prompt, ...imageParts]);
  const response = await result.response;
  const text = response.text();
  if (!text) {
    console.error(`Modelo ${modelName} não retornou texto.`);
    throw new Error(`Sem resposta do modelo ${modelName}`);
  }
  return text;
}

export async function processImagesWithGemini(files: File[], context?: string) {
  try {
    // Converter arquivos para o formato imageParts
    const imageParts = await Promise.all(
      files.map((file) => {
        return new Promise<{ inlineData: { data: string; mimeType: string } }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            // Regex para extrair mimeType e dados base64
            const match = base64String.match(/^data:(image\/[a-zA-Z0-9+.-]+);base64,(.+)$/);
            if (match && match[1] && match[2]) {
              resolve({
                inlineData: { data: match[2], mimeType: match[1] },
              });
            } else {
              // Fallback se regex falhar (similar ao código original, mas garantindo que mimeType não seja indefinido)
              console.warn(
                `Não foi possível extrair mimeType via regex para ${file.name}. Usando file.type como fallback.`
              );
              const b64data = base64String.includes(',') ? base64String.split(',')[1] : base64String;
              resolve({
                inlineData: { data: b64data, mimeType: file.type || "image/jpeg" }, // Default para image/jpeg se file.type for vazio
              });
            }
          };
          reader.onerror = (errorEvent) => {
            console.error(`Erro ao ler o arquivo ${file.name}:`, errorEvent);
            reject(new Error(`Erro ao ler o arquivo ${file.name}.`));
          };
          reader.readAsDataURL(file);
        });
      })
    );

    const contextPrompt = context ? `CONTEXTO: ${context}` : '';

    const prompt = `${contextPrompt}
    Por favor, interprete estas imagens de forma clara e objetiva, explicando detalhadamente o traçado do ECG e alterações, usando uma linguagem médica precisa. Me de sua impressão mesmo que ache que não seja capaz de interpretar. Depois me fale ao final o nível de confiança que você tem na sua interpretação. Não formate a resposta em HTML nem markdown, apenas retorne o texto.
    Nunca identifique o paciente por nome.
    Ao final inclua uma lista de possíveis diagnósticos e recomendações, se baseando em referências literárias médicas e guidelines médicas atuais dos últimos 5 anos. Referencie as fontes bibliográficas com links para o usuário poder se aprofundar no tema se quiser.
    Primeiro identifique adequadamente a orientação correta do ECG, cada uma das derivações e o ritmo.
    Preste muita atenção também a morfologia das ondas P e T em cada derivação.
    Faça a analise do traçado de cada uma das derivações individualmente. Preste muita atenção aos detalhes do traçado de cada derivação, pricipalmente a infra e supra de ST, e a morfologia das ondas P e T em cada derivação. Cheque cada uma individualmente, pode ser que a qualidade da imagem projudique na interpretação mas mesmo assim tome muito cuidado com cada detalhe.
    Também calcule os intervalos em cada derivação para verificar bloqueios. Leve em consideração que a imagem pode ter sido capturada em um ambiente com ruídos, o que pode dificultar a interpretação mas mesmo assim avalie bloqueios e ritmos.
    Se a imagem enviada não contiver um eletrocardiograma, me responda apenas "Essa imagem não parece ser um eletrocardiograma. Se ela é, tente enviar novamente com mais qualidade ou sem outros elementos na foto." e não retorne nada mais. Se contiver um ECG e outras coisas (como a tela de um computador), interprete apenas o ECG contido nela e retorne a resposta.
    Siga a seguinte estrutura à risca:
    
    # ANÁLISE DO TRAÇADO E RITMO
    
    # MEDIÇÕES E INTERVALOS
    
    # IMPRESSÃO DIAGNÓSTICA
    
    # DIAGNÓSTICOS DIFERENCIAIS
    
    # RECOMENDAÇÕES
    
    # REFERÊNCIAS BIBLIOGRÁFICAS
    [aqui inclua as referências bibliográficas que você utilizou para chegar aos diagnósticos e recomendações, com links para o usuário poder se aprofundar no tema se quiser.] 
    [traga também fontes brasileiras e guidelines brasileiras atrelados ao caso, sempre com links para o usuário poder se aprofundar no tema se quiser]
    
    # NÍVEL DE CONFIANÇA
    [aqui inclua o nível de confiança que você tem na sua interpretação da(s) imagem(ns) enviada(s), com porcentual e explicação do porque]`;

    let text: string;
    try {
      text = await generateContentWithModel(PRIMARY_MODEL_NAME, prompt, imageParts);
    } catch (primaryError: any) {
      const primaryErrorMessage = primaryError instanceof Error ? primaryError.message : String(primaryError);
      console.warn(
        `Falha ao processar com o modelo primário (${PRIMARY_MODEL_NAME}): ${primaryErrorMessage}`
      );
      try {
        console.log(`Tentando com o modelo de fallback: ${FALLBACK_MODEL_NAME}`);
        text = await generateContentWithModel(FALLBACK_MODEL_NAME, prompt, imageParts);
      } catch (fallbackError: any) {
        const fallbackErrorMessage = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
        console.warn(
          `Falha ao processar com o modelo de fallback (${FALLBACK_MODEL_NAME}): ${fallbackErrorMessage}`
        );
        try {
          console.log(`Tentando com o segundo modelo de fallback: ${SECOND_FALLBACK_MODEL_NAME}`);
          text = await generateContentWithModel(SECOND_FALLBACK_MODEL_NAME, prompt, imageParts);
        } catch (secondFallbackError: any) {
          const secondFallbackErrorMessage = secondFallbackError instanceof Error ? secondFallbackError.message : String(secondFallbackError);
          console.error(
            `Falha ao processar com o segundo modelo de fallback (${SECOND_FALLBACK_MODEL_NAME}): ${secondFallbackErrorMessage}`
          );
          // Mantendo a mensagem de erro genérica para o usuário final
          throw new Error(
            "Pedimos desculpas, no momento nossos servidores estão sobrecarregados. Para que possamos continuar oferecendo o serviço gratuitamente, pedimos que tente novamente mais tarde."
          );
        }
      }
    }

    if (typeof text !== 'string' || text.trim() === '') {
        console.error("Nenhum texto foi retornado pelos modelos após todas as tentativas.");
        throw new Error('Não foi possível processar as imagens no momento. Tente novamente.');
    }

    // Formatar o texto (lógica original mantida)
    const formattedText = text
      // Substituir linhas que começam com # por títulos
      .split('\n')
      .map(line => {
        if (line.startsWith('# ') && line.toUpperCase() === line) {
          return `<h2 class="text-2xl font-bold text-primary mt-8 mb-4 border-b pb-2">${line.substring(2)}</h2>`;
        }
        return line;
      })
      .join('\n')
      // Substituir ### por tags de seção
      .replace(/###\s*\*\*(\d+)\.\s*([^*]+)\*\*/g, '<h4 class="font-semibold text-lg mt-4">$1. $2</h4>')
      // Substituir ** por tags de destaque
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      // Tornar URLs clicáveis
      .replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary/80 underline">$1</a>')
      // Substituir quebras de linha por parágrafos (exceto para linhas que já são títulos)
      .split('\n')
      .map(line => {
        if (line.startsWith('<h')) return line;
        if (line.startsWith('http')) {
          return `<p class="mb-2"><a href="${line}" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary/80 underline">${line}</a></p>`;
        }
        return line.trim() ? `<p class="mb-2">${line}</p>` : '';
      })
      .filter(Boolean)
      .join('');

    // Retornar os resultados
    return {
      images: files.map((file) => ({
        url: URL.createObjectURL(file),
        diagnosis: formattedText,
        recommendations: null // Mantido como no original
      }))
    };
  } catch (error) {
    if (error instanceof Error) {
      console.error("Erro final capturado em processImagesWithGemini:", error.message);
    } else {
      console.error("Erro final capturado em processImagesWithGemini (tipo desconhecido):", String(error));
    }
    throw error; // Re-lançar o erro para ser tratado pelo chamador
  }
} 